import type { ActionFieldValue } from "../ActionFieldEditor";

/**
 * previewUtils — shared helpers for the vendor action-preview cards.
 *
 * Before this module, every FiC / SDI preview byte-duplicated the same
 * it-IT EUR formatter, the per-key ``executed ?? proposed`` resolver, the
 * IBAN 4-char grouping, and the payment-method label map. DRY #6: single
 * source of truth so a copy can never drift from another.
 */

/**
 * Shared it-IT EUR formatter instance. `maximumFractionDigits: 2` keeps the
 * two-decimal contract for monetary figures (invoices, payments, totals).
 */
const EUR_FORMATTER = new Intl.NumberFormat("it-IT", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 2,
});

/**
 * Format an ``ActionFieldValue`` as an it-IT EUR string.
 *
 * Accepts a number or a numeric string. A non-numeric string passes through
 * verbatim (some BE payloads pre-format), and ``null``/``undefined``/NaN
 * collapse to the em-dash placeholder.
 */
export function fmtCurrencyEUR(value: ActionFieldValue | undefined): string {
  if (value === undefined || value === null) return "—";
  const n =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number(value)
        : Number.NaN;
  if (!Number.isFinite(n)) return typeof value === "string" ? value : "—";
  return EUR_FORMATTER.format(n);
}

/**
 * Per-key resolver for the proposed/executed pair.
 *
 * Returns the executed value for ``key`` if present, otherwise the proposed
 * one, coerced to a string (non-string values yield ``""``). This is the
 * per-key replacement for the unsafe whole-object ``executed ?? proposed``
 * spread which silently drops unchanged fields.
 */
export function pickField(
  proposed: Readonly<Record<string, ActionFieldValue>>,
  executed: Readonly<Record<string, ActionFieldValue>> | undefined,
  key: string,
): string {
  const v = executed?.[key] ?? proposed[key];
  return typeof v === "string" ? v : "";
}

/**
 * Raw (uncoerced) per-key resolver — returns the executed-or-proposed value
 * without string coercion, for callers that need numbers/arrays (e.g. amount
 * fields or line-item arrays).
 */
export function pickFieldRaw(
  proposed: Readonly<Record<string, ActionFieldValue>>,
  executed: Readonly<Record<string, ActionFieldValue>> | undefined,
  key: string,
): ActionFieldValue | undefined {
  return executed?.[key] ?? proposed[key];
}

/** Group an IBAN into uppercase 4-char blocks for legibility (display-only). */
export function groupIban(raw: string): string {
  const clean = raw.replace(/\s+/g, "").toUpperCase();
  return clean.replace(/(.{4})/g, "$1 ").trim();
}

/** Payment-method wire value → italian human label. */
export const METHOD_LABEL: Readonly<Record<string, string>> = {
  bank_transfer: "Bonifico bancario",
  cash: "Contanti",
  check: "Assegno",
  card: "Carta di credito",
  rid: "RID / SEPA Direct Debit",
  sdd: "SEPA Direct Debit",
  paypal: "PayPal",
  stripe: "Stripe",
};
